// FILE: TutorialDashboard.js
import React, { useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Avatar,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
    Badge,
    LinearProgress
} from "@mui/material";
import {
    PlayArrow as PlayIcon,
    Quiz as QuizIcon,
    Description as GuideIcon,
    Close as CloseIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    Lightbulb as TipIcon,
    Speed as SpeedIcon,
    School as SchoolIcon,
    Assessment as AssessmentIcon,
    Star as StarIcon,
    CheckCircle as CheckIcon
} from "@mui/icons-material";
import InteractiveGettingStartedGuide from "./InteractiveGettingStartedGuide";

const TutorialDashboard = ({ onTutorialStart, activeTutorial }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedTutorial, setSelectedTutorial] = useState(null);
    const [completedTutorials, setCompletedTutorials] = useState(new Set(['quick-tips'])); // Example: user completed quick tips

    const tutorials = [
        {
            id: 'interactive-guide',
            title: 'Interactive Quick Start',
            description: 'Step-by-step instructions to add students, create subjects/units, record DOK assessment and generate comments.',
            duration: '20 min',
            difficulty: 'All Levels',
            type: 'interactive',
            icon: <TrophyIcon />,
            color: theme.palette.primary.main,
            features: [
                'Adding Students',
                'Creating Subjects/Units',
                'Recording DOK Assessments for Single/Multiple Students',
                'Generate Comments'
            ],
            component: InteractiveGettingStartedGuide,
            featured: true
        }
    ];

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'Beginner': return theme.palette.success.main;
            case 'Advanced': return theme.palette.error.main;
            case 'Power Users': return theme.palette.warning.main;
            default: return theme.palette.info.main;
        }
    };

    const getTypeIcon = (type) => {
        const icons = {
            interactive: <SpeedIcon />,
            documentation: <GuideIcon />,
            tips: <TipIcon />,
            advanced: <AssessmentIcon />,
            quiz: <QuizIcon />
        };
        return icons[type] || <GuideIcon />;
    };

    const handleTutorialSelect = (tutorial) => {
        if (tutorial.comingSoon) return;

        if (tutorial.id === 'interactive-guide') {
            // Start the persistent tutorial
            onTutorialStart?.(tutorial.id);
        } else {
            // Regular modal tutorials
            setSelectedTutorial(tutorial);
        }
    };

    const handleTutorialComplete = (tutorialId) => {
        setCompletedTutorials(prev => new Set([...prev, tutorialId]));
    };


    const completionPercentage = (completedTutorials.size / tutorials.filter(t => !t.comingSoon).length) * 100;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Paper elevation={3} sx={{
                p: 3,
                mb: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                borderRadius: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 3, width: 64, height: 64 }}>
                        <SchoolIcon sx={{ fontSize: 36 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary" mb={1}>
                            Gradely Tutorial Center
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Follow our Interactive Quick Start Guide for step-by-step instructions to master Gradely
                        </Typography>
                    </Box>
                </Box>

                {/* Progress Overview */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Overall Progress</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {completedTutorials.size} of {tutorials.filter(t => !t.comingSoon).length} completed
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={completionPercentage}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.success.main
                            }
                        }}
                    />
                </Box>

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Chip
                        icon={<TrophyIcon />}
                        label={`${Math.round(completionPercentage)}% Complete`}
                        color="primary"
                        variant={completionPercentage === 100 ? "filled" : "outlined"}
                    />
                    <Chip
                        icon={<TimerIcon />}
                        label="Self-paced learning"
                        color="secondary"
                    />

                </Stack>
            </Paper>

            {/* Tutorial Cards */}
            <Grid container spacing={3}>
                {tutorials.map((tutorial) => {
                    const isCompleted = completedTutorials.has(tutorial.id);
                    const isFeatured = tutorial.featured;

                    return (
                        <Grid item xs={12} md={6} lg={4} key={tutorial.id}>
                            <Card
                                elevation={isFeatured ? 4 : 2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    cursor: tutorial.comingSoon ? 'not-allowed' : 'pointer',
                                    opacity: tutorial.comingSoon ? 0.7 : 1,
                                    border: isFeatured ? `2px solid ${theme.palette.primary.main}` : 'none',
                                    '&:hover': {
                                        transform: tutorial.comingSoon ? 'none' : 'translateY(-4px)',
                                        boxShadow: tutorial.comingSoon ? 2 : 6
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => handleTutorialSelect(tutorial)}
                            >
                                {/* Featured Badge */}
                                {isFeatured && (
                                    <Chip
                                        icon={<StarIcon />}
                                        label="Recommended"
                                        color="primary"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            zIndex: 1
                                        }}
                                    />
                                )}

                                {/* Completed Badge */}
                                {isCompleted && (
                                    <Badge
                                        badgeContent={<CheckIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            left: 12,
                                            zIndex: 1,
                                            '& .MuiBadge-badge': {
                                                backgroundColor: theme.palette.success.main,
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <Box />
                                    </Badge>
                                )}

                                <CardContent sx={{ flex: 1, pt: isFeatured ? 4 : 3 }}>
                                    {/* Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{
                                            bgcolor: tutorial.color,
                                            mr: 2,
                                            width: 48,
                                            height: 48
                                        }}>
                                            {tutorial.icon}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" fontWeight="bold" mb={0.5}>
                                                {tutorial.title}
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    icon={getTypeIcon(tutorial.type)}
                                                    label={tutorial.type}
                                                    size="small"
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </Stack>
                                        </Box>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        {tutorial.description}
                                    </Typography>

                                    {/* Features */}
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                            What you'll learn:
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {tutorial.features.slice(0, 3).map((feature, idx) => (
                                                <Typography key={idx} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CheckIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.success.main }} />
                                                    {feature}
                                                </Typography>
                                            ))}
                                            {tutorial.features.length > 3 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    +{tutorial.features.length - 3} more features
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>

                                    {/* Metadata */}
                                    <Stack direction="row" spacing={2} mt="auto">
                                        <Chip
                                            icon={<TimerIcon />}
                                            label={tutorial.duration}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={tutorial.difficulty}
                                            size="small"
                                            sx={{
                                                backgroundColor: getDifficultyColor(tutorial.difficulty) + '20',
                                                color: getDifficultyColor(tutorial.difficulty),
                                                fontWeight: 600
                                            }}
                                        />
                                    </Stack>
                                </CardContent>

                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={tutorial.comingSoon ? <TimerIcon /> : <PlayIcon />}
                                        disabled={tutorial.comingSoon}
                                        sx={{
                                            backgroundColor: tutorial.comingSoon ? theme.palette.grey[400] : tutorial.color,
                                            '&:hover': {
                                                backgroundColor: tutorial.comingSoon ? theme.palette.grey[400] : tutorial.color + 'dd'
                                            }
                                        }}
                                    >
                                        {tutorial.comingSoon ? 'Coming Soon' :
                                            isCompleted ? 'Review Tutorial' : 'Start Tutorial'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Tutorial Dialog */}
            {/* Regular Tutorial Dialog */}
            <Dialog
                open={!!selectedTutorial}
                onClose={() => setSelectedTutorial(null)}
                maxWidth="xl"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        maxHeight: '95vh'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {selectedTutorial && (
                            <Avatar sx={{ bgcolor: selectedTutorial.color, mr: 2 }}>
                                {selectedTutorial.icon}
                            </Avatar>
                        )}
                        <Typography variant="h6" fontWeight="bold">
                            {selectedTutorial?.title}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setSelectedTutorial(null)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                    {selectedTutorial && selectedTutorial.component && (
                        <selectedTutorial.component
                            onComplete={() => {
                                handleTutorialComplete(selectedTutorial.id);
                                setSelectedTutorial(null);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TutorialDashboard;